import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { addWeeks, format, subWeeks } from "date-fns";
import toast from "react-hot-toast";
import { Calculator, Pencil, Plus, Trash2 } from "lucide-react";
import { AppModalFooter, AppModalShell } from "../components/app-ui.js";
import NotifyDayDialog from "../components/NotifyDayDialog.js";
import { Button } from "../components/legacy-ui.js";
import {
  createCheeseProduction,
  deleteCheeseProduction,
  getCheeseSummary,
  pushAdminNotification,
  updateCheeseProduction,
} from "../utils/api.js";
import { parseEsVeNumber } from "../utils/formatters.js";
import { companyPageTitle } from "../utils/company.js";
import { exportCheeseWeekExcel } from "../utils/exportCheeseWeekExcel.js";
import { LoadingState, SkeletonRows } from "../components/motion.js";

const fmt = (value, decimals = 2) =>
  Number(value || 0).toLocaleString("es-VE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const emptyMilkRow = () => ({ vaca: "", bufalo: "" });

const AddMilkEditor = ({ provider, initialRows, onClose, onApply }) => {
  const [rows, setRows] = useState(() => {
    const source = initialRows?.length ? initialRows : [emptyMilkRow()];
    return source.map((row) => ({
      vaca: row.vaca ? String(row.vaca) : "",
      bufalo: row.bufalo ? String(row.bufalo) : "",
    }));
  });
  const [error, setError] = useState("");

  const totals = rows.reduce(
    (acc, row) => ({
      vaca: acc.vaca + (parseEsVeNumber(row.vaca) || 0),
      bufalo: acc.bufalo + (parseEsVeNumber(row.bufalo) || 0),
    }),
    { vaca: 0, bufalo: 0 },
  );

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    setError("");
  };

  const apply = (keep) => {
    if (!keep) {
      onApply([]);
      return;
    }
    for (let i = 0; i < rows.length; i += 1) {
      const vacaRaw = String(rows[i].vaca || "").trim();
      const bufaloRaw = String(rows[i].bufalo || "").trim();
      if (vacaRaw && parseEsVeNumber(vacaRaw) == null) {
        setError(`Número inválido en vaca, línea ${i + 1}.`);
        return;
      }
      if (bufaloRaw && parseEsVeNumber(bufaloRaw) == null) {
        setError(`Número inválido en búfala, línea ${i + 1}.`);
        return;
      }
    }
    const cleaned = [];
    rows.forEach((row) => {
      const vaca = parseEsVeNumber(row.vaca) || 0;
      const bufalo = parseEsVeNumber(row.bufalo) || 0;
      if (vaca <= 0 && bufalo <= 0) return;
      cleaned.push({
        vaca: Math.round(vaca * 100) / 100,
        bufalo: Math.round(bufalo * 100) / 100,
      });
    });
    onApply(cleaned);
  };

  return (
    <AppModalShell
      open
      onClose={onClose}
      title={`Agregar leche — ${provider.name}`}
      widthClass="max-w-md w-full"
      fitContent
      zClass="z-[90]"
      footer={
        <AppModalFooter>
          <Button type="button" variant="secondary" onClick={() => apply(false)}>
            Quitar leche
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => apply(true)}>
              Usar totales
            </Button>
          </div>
        </AppModalFooter>
      }
    >
      <p className="text-sm text-gray-500 mb-3">
        Anota cada litraje de vaca y búfala. Al aceptar, se suma y se pone en Usar.
      </p>
      <p className="text-xs text-gray-500 mb-3">
        Restante disponible: vaca {fmt(provider.remainingVaca)} L · búfala {fmt(provider.remainingBufalo)} L
      </p>
      <div className="grid grid-cols-[2.5rem_1fr_1fr_auto] gap-2 items-center text-xs font-semibold text-gray-500 mb-1">
        <span>#</span>
        <span className="text-center">Vaca</span>
        <span className="text-center">Búfala</span>
        <span />
      </div>
      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={index} className="grid grid-cols-[2.5rem_1fr_1fr_auto] gap-2 items-center">
            <span className="text-sm text-center text-gray-600">{index + 1}</span>
            <input
              type="text"
              inputMode="decimal"
              className="w-full text-center text-sm bg-white border border-gray-300 rounded px-2 py-1.5 outline-none focus:border-emerald-600"
              value={row.vaca}
              onChange={(e) => updateRow(index, "vaca", e.target.value)}
            />
            <input
              type="text"
              inputMode="decimal"
              className="w-full text-center text-sm bg-white border border-gray-300 rounded px-2 py-1.5 outline-none focus:border-emerald-600"
              value={row.bufalo}
              onChange={(e) => updateRow(index, "bufalo", e.target.value)}
            />
            <button
              type="button"
              className="text-xs text-red-600 px-1 py-1"
              onClick={() =>
                setRows((prev) =>
                  prev.length === 1 ? [emptyMilkRow()] : prev.filter((_, i) => i !== index),
                )
              }
            >
              Quitar
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="mt-3 text-sm font-semibold text-emerald-700"
        onClick={() => setRows((prev) => [...prev, emptyMilkRow()])}
      >
        Agregar leche
      </button>
      <p className="mt-3 text-sm text-gray-700">
        Total: vaca {fmt(totals.vaca)} L · búfala {fmt(totals.bufalo)} L
      </p>
      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
    </AppModalShell>
  );
};

const fmtPromedio = (value) => (value == null || value === "" ? "—" : `${fmt(value)} L/kg`);

const optionalPromedioBound = (value) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 ? num : null;
};

const promedioAlertOf = (promedio, min, max) => {
  if (promedio == null || !Number.isFinite(Number(promedio))) return null;
  const value = Number(promedio);
  const maxBound = optionalPromedioBound(max);
  const minBound = optionalPromedioBound(min);
  if (maxBound != null && value - maxBound > 0.009) return "high";
  if (minBound != null && minBound - value > 0.009) return "low";
  return null;
};

const productPromedioBounds = (product) => ({
  min: optionalPromedioBound(product?.advanced?.promedioMin ?? product?.promedioMin),
  max: optionalPromedioBound(product?.advanced?.promedioMax ?? product?.promedioMax),
});

const fmtPromedioRange = (min, max) => {
  const hasMin = min != null;
  const hasMax = max != null;
  if (!hasMin && !hasMax) return "";
  if (hasMin && hasMax) return `${fmt(min)} – ${fmt(max)} L/kg`;
  if (hasMax) return `máx. ${fmt(max)} L/kg`;
  return `mín. ${fmt(min)} L/kg`;
};

const promedioAlertClass = (alert) => {
  if (alert === "high") return "text-amber-800 font-semibold";
  if (alert === "low") return "text-sky-800 font-semibold";
  return "";
};

const promedioAlertLabel = (alert, min, max) => {
  if (alert === "high") return `Por encima del rango (${fmtPromedioRange(min, max)})`;
  if (alert === "low") return `Por debajo del rango (${fmtPromedioRange(min, max)})`;
  return "";
};

const dateKey = (value) => String(value || "").slice(0, 10);

const fmtDay = (value) => {
  const key = dateKey(value);
  if (key.length < 10) return "";
  const [, month, day] = key.split("-");
  return `${day}/${month}`;
};

const fmtDate = (value) => {
  const key = dateKey(value);
  if (key.length < 10) return "";
  const [year, month, day] = key.split("-");
  return `${day}/${month}/${year}`;
};

const weekNavLabel = (data) => {
  if (!data) return "Cargando semana…";
  const days = data.days || [];
  const start = days[0]?.date || data.startOfWeek;
  const end = days[6]?.date || data.endOfWeek;
  const year = dateKey(end).slice(0, 4);
  return `Jueves ${fmtDay(start)} — Miércoles ${fmtDay(end)} ${year}`;
};

const oid = (value) => {
  if (value && typeof value === "object") return String(value._id || value.id || "");
  return String(value || "");
};

const productName = (value) => {
  if (value && typeof value === "object") return value.name || "";
  return String(value || "");
};

const emptyTypeGroup = (id, name) => ({
  id,
  name,
  rows: [],
  litersVaca: 0,
  litersBufalo: 0,
  totalLiters: 0,
  kg: 0,
  cheeseCount: 0,
  promedio: null,
});

const groupProductionsByDay = (productions, weekDays = []) => {
  const labels = {};
  weekDays.forEach((day) => {
    labels[dateKey(day.date)] = day.label || "";
  });
  const order = weekDays.map((day) => dateKey(day.date)).filter(Boolean);
  const groups = {};
  productions.forEach((row) => {
    const key = dateKey(row.date);
    if (!key) return;
    if (!groups[key]) {
      groups[key] = { date: key, label: labels[key] || "", types: {} };
    }
    const typeId = oid(row.product) || productName(row.product) || "queso";
    const typeName = productName(row.product) || "Queso";
    if (!groups[key].types[typeId]) {
      groups[key].types[typeId] = emptyTypeGroup(typeId, typeName);
    }
    const type = groups[key].types[typeId];
    type.rows.push(row);
    type.litersVaca += Number(row.litersVaca) || 0;
    type.litersBufalo += Number(row.litersBufalo) || 0;
    type.totalLiters += Number(row.totalLiters) || 0;
    type.kg += Number(row.kg) || 0;
    type.cheeseCount += Number(row.cheeseCount) || 0;
  });

  const extra = Object.keys(groups).filter((key) => !order.includes(key)).sort();
  return [...order, ...extra]
    .filter((key) => groups[key])
    .map((key) => {
      const group = groups[key];
      const types = Object.values(group.types)
        .map((type) => ({
          ...type,
          promedio: type.kg > 0.009 ? type.totalLiters / type.kg : null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name, "es"));
      return { date: group.date, label: group.label, types };
    });
};

const milkStatus = (row) => {
  if (Number(row.remaining) > 0.01) return "pending";
  if (Number(row.used) > 0.01) return "assigned";
  return "idle";
};

const availableProviders = (providers, production) => {
  const byId = {};
  (providers || []).forEach((row) => {
    byId[String(row.id)] = {
      ...row,
      remainingVaca: Number(row.remainingVaca) || 0,
      remainingBufalo: Number(row.remainingBufalo) || 0,
    };
  });
  (production?.allocations || []).forEach((alloc) => {
    const pid = oid(alloc.provider);
    if (!pid) return;
    if (!byId[pid]) {
      byId[pid] = {
        id: pid,
        name: alloc.provider?.name || "Proveedor",
        remainingVaca: 0,
        remainingBufalo: 0,
      };
    }
    const liters = Number(alloc.liters) || 0;
    if (alloc.milkType === "bufalo") byId[pid].remainingBufalo += liters;
    else byId[pid].remainingVaca += liters;
  });
  return Object.values(byId).sort((a, b) => {
    const ra = (a.remainingVaca || 0) + (a.remainingBufalo || 0);
    const rb = (b.remainingVaca || 0) + (b.remainingBufalo || 0);
    if (rb !== ra) return rb - ra;
    return String(a.name || "").localeCompare(String(b.name || ""), "es");
  });
};

const numUse = (uses, pid, milkType) => parseEsVeNumber(uses?.[`${pid}:${milkType}`]) || 0;

const litersInput = (value) => {
  const n = Math.round((Number(value) || 0) * 100) / 100;
  if (n <= 0) return "";
  return String(n);
};

const nameFromProviders = (rows) =>
  rows
    .map((row) => String(row.name || "").trim())
    .filter(Boolean)
    .join(", ")
    .slice(0, 120);

const nameTokens = (name) =>
  String(name || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

const emptyForm = () => ({
  date: "",
  product: "",
  name: "",
  kg: "",
  cheeseCount: "",
  uses: {},
});

const CheesesModule = () => {
  const [data, setData] = useState(null);
  const [weekDate, setWeekDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [tab, setTab] = useState("products");
  const [notifying, setNotifying] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [milkEditor, setMilkEditor] = useState(null);
  const [milkLines, setMilkLines] = useState({});
  const nameSource = useRef("auto");

  useEffect(() => {
    document.title = companyPageTitle("Quesos");
  }, []);

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const res = await getCheeseSummary({
          closeDay: 3,
          date: format(weekDate, "yyyy-MM-dd"),
        });
        if (res.data.success) setData(res.data.data);
      } catch (error) {
        console.error(error);
        toast.error(error.response?.data?.error || "No se pudo cargar Quesos");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [weekDate],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const providers = useMemo(() => data?.providers || [], [data?.providers]);
  const productions = useMemo(() => data?.productions || [], [data?.productions]);
  const products = useMemo(() => data?.products || [], [data?.products]);
  const byProduct = data?.byProduct || [];
  const totals = data?.totals || {};
  const days = useMemo(() => data?.days || [], [data?.days]);
  const productionDays = useMemo(
    () => groupProductionsByDay(productions, days),
    [productions, days],
  );

  const available = useMemo(
    () => availableProviders(providers, editing),
    [providers, editing],
  );

  const selectedProduct = useMemo(
    () => products.find((item) => String(item._id) === String(form.product)) || null,
    [products, form.product],
  );

  const live = useMemo(() => {
    let totalVaca = 0;
    let totalBufalo = 0;
    let over = false;
    available.forEach((row) => {
      const vaca = numUse(form.uses, row.id, "vaca");
      const bufalo = numUse(form.uses, row.id, "bufalo");
      if (vaca - (row.remainingVaca || 0) > 0.009) over = true;
      if (bufalo - (row.remainingBufalo || 0) > 0.009) over = true;
      totalVaca += vaca;
      totalBufalo += bufalo;
    });
    const total = totalVaca + totalBufalo;
    const kg = parseEsVeNumber(form.kg) || 0;
    const promedio = kg > 0.009 ? total / kg : null;
    const bounds = productPromedioBounds(selectedProduct);
    return {
      totalVaca,
      totalBufalo,
      total,
      over,
      promedio,
      promedioMin: bounds.min,
      promedioMax: bounds.max,
      promedioAlert: promedioAlertOf(promedio, bounds.min, bounds.max),
    };
  }, [available, form.uses, form.kg, selectedProduct]);

  const openCreate = (productId = "") => {
    if (!products.length) {
      toast.error("Carga productos antes de registrar una producción.");
      return;
    }
    if (!providers.length) {
      toast.error("No hay litrajes cargados en esta semana. Cárgalos en Leche primero.");
      return;
    }
    const today = format(new Date(), "yyyy-MM-dd");
    const dayKeys = days.map((d) => dateKey(d.date));
    setEditing(null);
    nameSource.current = "auto";
    setForm({
      ...emptyForm(),
      date: dayKeys.includes(today) ? today : dayKeys[0] || today,
      product: productId || products[0]?._id || "",
    });
    setFormOpen(true);
  };

  const openEdit = (production) => {
    const uses = {};
    (production.allocations || []).forEach((alloc) => {
      const pid = oid(alloc.provider);
      if (!pid) return;
      uses[`${pid}:${alloc.milkType === "bufalo" ? "bufalo" : "vaca"}`] =
        alloc.liters ? String(alloc.liters) : "";
    });
    setEditing(production);
    nameSource.current = String(production.name || "").trim() ? "manual" : "auto";
    setForm({
      date: dateKey(production.date),
      product: oid(production.product),
      name: production.name || "",
      kg: production.kg ? String(production.kg) : "",
      cheeseCount: production.cheeseCount ? String(production.cheeseCount) : "",
      uses,
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setMilkEditor(null);
    setMilkLines({});
    nameSource.current = "auto";
    setForm(emptyForm());
  };

  const setUse = (pid, milkType, raw) => {
    setForm((prev) => {
      const uses = { ...prev.uses, [`${pid}:${milkType}`]: raw };
      if (nameSource.current !== "auto") return { ...prev, uses };
      const picked = available.filter(
        (row) => numUse(uses, row.id, "vaca") > 0 || numUse(uses, row.id, "bufalo") > 0,
      );
      return { ...prev, uses, name: nameFromProviders(picked) };
    });
  };

  const milkRowsForProvider = (row) => {
    const saved = milkLines[row.id];
    if (saved?.length) return saved;
    const vaca = form.uses[`${row.id}:vaca`] || "";
    const bufalo = form.uses[`${row.id}:bufalo`] || "";
    if (String(vaca).trim() || String(bufalo).trim()) return [{ vaca, bufalo }];
    return [emptyMilkRow()];
  };

  const applyMilkLines = (row, lines) => {
    const totalVaca = lines.reduce((sum, line) => sum + (Number(line.vaca) || 0), 0);
    const totalBufalo = lines.reduce((sum, line) => sum + (Number(line.bufalo) || 0), 0);
    setMilkLines((prev) => {
      const next = { ...prev };
      if (lines.length) next[row.id] = lines;
      else delete next[row.id];
      return next;
    });
    setForm((prev) => {
      const uses = {
        ...prev.uses,
        [`${row.id}:vaca`]: litersInput(totalVaca),
        [`${row.id}:bufalo`]: litersInput(totalBufalo),
      };
      if (nameSource.current !== "auto") return { ...prev, uses };
      const picked = available.filter(
        (item) => numUse(uses, item.id, "vaca") > 0 || numUse(uses, item.id, "bufalo") > 0,
      );
      return { ...prev, uses, name: nameFromProviders(picked) };
    });
    setMilkEditor(null);
  };

  const toggleNameProvider = (row) => {
    nameSource.current = "chips";
    setForm((prev) => {
      const tokens = nameTokens(prev.name);
      const folded = tokens.map((token) => token.toLowerCase());
      const name = String(row.name || "").trim();
      const next = folded.includes(name.toLowerCase())
        ? tokens.filter((token) => token.toLowerCase() !== name.toLowerCase())
        : [...tokens, name];
      return { ...prev, name: next.join(", ").slice(0, 120) };
    });
  };

  const applyLastProviders = () => {
    const last = [...productions]
      .reverse()
      .find((prod) => !editing || prod._id !== editing._id);
    if (!last) {
      toast.error("No hay una producción anterior esta semana");
      return;
    }
    const previous = String(last.name || "").trim();
    if (previous) {
      nameSource.current = "chips";
      setForm((prev) => ({ ...prev, name: previous.slice(0, 120) }));
      return;
    }
    const ids = new Set((last.allocations || []).map((alloc) => oid(alloc.provider)).filter(Boolean));
    nameSource.current = "chips";
    setForm((prev) => ({
      ...prev,
      name: nameFromProviders(available.filter((row) => ids.has(row.id))),
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const allocations = [];
    const overs = [];
    available.forEach((row) => {
      [
        ["vaca", row.remainingVaca, "vaca"],
        ["bufalo", row.remainingBufalo, "búfala"],
      ].forEach(([milkType, remaining, label]) => {
        const liters = numUse(form.uses, row.id, milkType);
        if (liters <= 0) return;
        if (liters - (remaining || 0) > 0.009) overs.push(`${row.name} (${label})`);
        allocations.push({ provider: row.id, milkType, liters: Math.round(liters * 100) / 100 });
      });
    });
    if (overs.length) {
      toast.error(`Hay litrajes mayores al restante: ${overs.join(", ")}`);
      return;
    }
    if (!allocations.length) {
      toast.error("Asigna al menos un litraje de leche");
      return;
    }
    if (!form.product || !form.date) {
      toast.error("Selecciona producto y fecha");
      return;
    }
    setSaving(true);
    const payload = {
      date: form.date,
      product: form.product,
      name: String(form.name || "").trim(),
      kg: parseEsVeNumber(form.kg) || 0,
      cheeseCount: Math.round(parseEsVeNumber(form.cheeseCount) || 0),
      allocations,
    };
    try {
      if (editing?._id) await updateCheeseProduction(editing._id, payload);
      else await createCheeseProduction(payload);
      toast.success(editing ? "Producción actualizada" : "Producción guardada");
      if (live.promedioAlert === "high") {
        toast(
          `Promedio alto: ${fmtPromedio(live.promedio)} (rango ${fmtPromedioRange(live.promedioMin, live.promedioMax)})`,
          { icon: "⚠️", duration: 5000 },
        );
      } else if (live.promedioAlert === "low") {
        toast(
          `Promedio bajo: ${fmtPromedio(live.promedio)} (rango ${fmtPromedioRange(live.promedioMin, live.promedioMax)})`,
          { icon: "ℹ️", duration: 5000 },
        );
      }
      closeForm();
      setTab("productions");
      await fetchData({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const notifyDays = useMemo(() => {
    return (days || []).map((day) => {
      let kg = 0;
      let liters = 0;
      let count = 0;
      const byType = {};
      productions.forEach((prod) => {
        if (dateKey(prod.date) !== day.date) return;
        const prodKg = Number(prod.kg) || 0;
        const prodLiters = Number(prod.totalLiters) || 0;
        kg += prodKg;
        liters += prodLiters;
        count += 1;
        const id = oid(prod.product);
        if (!id) return;
        if (!byType[id]) {
          byType[id] = {
            id,
            name: productName(prod.product) || "Producto",
            kg: 0,
            liters: 0,
            count: 0,
          };
        }
        byType[id].kg += prodKg;
        byType[id].liters += prodLiters;
        byType[id].count += 1;
      });
      const promedio = kg > 0.009 ? liters / kg : null;
      const enabled = count > 0 && promedio != null;
      const products = Object.values(byType)
        .map((row) => {
          const rowPromedio = row.kg > 0.009 ? row.liters / row.kg : null;
          const rowEnabled = row.count > 0 && rowPromedio != null;
          return {
            id: row.id,
            name: row.name,
            enabled: rowEnabled,
            meta: rowEnabled
              ? `${fmtPromedio(rowPromedio)} · ${fmt(row.kg)} kg`
              : row.count > 0
                ? "Sin promedio"
                : "Sin quesos",
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name, "es"));
      return {
        date: day.date,
        label: `${day.label} ${fmtDay(day.date)}`,
        enabled,
        meta: enabled
          ? `${fmtPromedio(promedio)} · ${fmt(kg)} kg`
          : count > 0
            ? "Sin promedio"
            : "Sin quesos",
        products,
      };
    });
  }, [days, productions]);

  const canNotifyCheese = notifyDays.some((day) => day.enabled);

  const handleNotifyAdmins = () => {
    if (!canNotifyCheese) {
      toast.error("Registra los quesos de un día con kilogramos para tener el promedio.");
      return;
    }
    setNotifyOpen(true);
  };

  const handleNotifyDay = async (date, productId) => {
    if (!date) return;
    setNotifying(true);
    try {
      await pushAdminNotification({
        type: "cheese",
        date,
        productId: productId || undefined,
        source: "web",
      });
      setNotifyOpen(false);
      toast.success("Se notificó por Telegram y WhatsApp");
      window.dispatchEvent(new Event("notifications:refresh"));
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo enviar la notificación");
    } finally {
      setNotifying(false);
    }
  };

  const handleDelete = async (production) => {
    const name = String(production.name || "").trim() || productName(production.product) || "esta producción";
    if (!window.confirm(`¿Eliminar ${name}?`)) return;
    try {
      await deleteCheeseProduction(production._id);
      toast.success("Producción eliminada");
      await fetchData({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo eliminar");
    }
  };

  const handleExportExcel = async () => {
    if (!data) {
      toast.error("No hay datos de la semana para exportar.", { position: "top-center" });
      return;
    }
    setExporting(true);
    try {
      await exportCheeseWeekExcel(data);
      toast.success("Excel de quesos descargado", { position: "top-center" });
    } catch (error) {
      toast.error(error.message || "No se pudo exportar el reporte.", { position: "top-center" });
    } finally {
      setExporting(false);
    }
  };

  const rowTone = (status, selected = false) => {
    if (selected) return "bg-[#d4ebdd]";
    if (status === "assigned") return "bg-green-50 hover:bg-green-100";
    if (status === "pending") return "bg-yellow-50 hover:bg-yellow-100";
    return "bg-red-50 hover:bg-red-100";
  };

  return (
    <>
      <Helmet>
        <title>{companyPageTitle("Quesos")}</title>
      </Helmet>
      <div className="p-4 max-w-7xl mx-auto flex flex-col min-h-0 pb-24 md:pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
          <h1 className="text-xl font-semibold text-gray-900">Quesos</h1>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={openCreate}>
              <Plus size={16} /> Nueva producción
            </Button>
            <Button
              variant="secondary"
              onClick={handleExportExcel}
              disabled={exporting || !data}
              title="Exportar reporte semanal de quesos a Excel"
            >
              {exporting ? "Exportando…" : "Excel"}
            </Button>
            <Button
              variant="secondary"
              onClick={handleNotifyAdmins}
              disabled={notifying || !canNotifyCheese}
              title={
                canNotifyCheese
                  ? "Enviar los quesos de un día a los administradores"
                  : "Registra quesos de un día con kilogramos para calcular el promedio"
              }
            >
              {notifying ? "Notificando…" : "Notificar admins"}
            </Button>
            <Button variant="secondary" onClick={() => fetchData()}>
              Actualizar
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3 text-sm text-gray-500">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setWeekDate((d) => subWeeks(d, 1))}
            aria-label="Semana anterior"
          >
            {"<"}
          </Button>
          <span className="min-w-[260px]">{weekNavLabel(data)}</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setWeekDate((d) => addWeeks(d, 1))}
            aria-label="Semana siguiente"
          >
            {">"}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setWeekDate(new Date())}>
            Hoy
          </Button>
          <span>Usa los litrajes de la semana (jueves a miércoles)</span>
        </div>

        <p className="text-sm text-gray-600 mb-1">
          Recibido: {fmt(totals.received)} L{"    "}
          Usado: {fmt(totals.used)} L{"    "}
          Restante: {fmt(totals.remaining)} L{"    "}
          Kg: {fmt(totals.kg)}{"    "}
          Promedio: {fmtPromedio(totals.promedio)}{"    "}
          Quesos: {fmt(totals.cheeseCount, 0)}
        </p>
        <p className="text-xs text-gray-500 mb-4">
          Recibido es lo que trajo cada proveedor (litros físicos). El descuento por agua solo afecta el pago, no la producción.
        </p>

        <div className="flex flex-wrap gap-1 mb-3 border-b border-gray-200">
          {[
            { id: "products", label: "Por producto" },
            { id: "providers", label: "Leche de proveedores" },
            { id: "productions", label: "Detalle de producciones" },
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === item.id
                  ? "border-emerald-700 text-emerald-800"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {tab === "products" && (
        <>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
          <div className="overflow-auto max-h-[min(70vh,640px)]">
            <table className="w-full text-sm text-left hidden sm:table">
              <thead className="bg-[#e7efe9] text-gray-800 sticky top-0">
                <tr>
                  <th className="px-4 py-2 font-semibold">Producto</th>
                  <th className="px-4 py-2 font-semibold text-right">Vaca (L)</th>
                  <th className="px-4 py-2 font-semibold text-right">Búfala (L)</th>
                  <th className="px-4 py-2 font-semibold text-right">Total L</th>
                  <th className="px-4 py-2 font-semibold text-right">Kg</th>
                  <th className="px-4 py-2 font-semibold text-right">Promedio</th>
                  <th className="px-4 py-2 font-semibold text-right">Quesos</th>
                  <th className="px-4 py-2 font-semibold text-right"> </th>
                </tr>
              </thead>
              <tbody>
                {byProduct.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                      No hay productos cargados en el sistema.
                    </td>
                  </tr>
                ) : (
                  byProduct.map((row) => {
                    const active = Number(row.batches) > 0 || Number(row.totalLiters) > 0.01;
                    const alert = row.promedioAlert || promedioAlertOf(row.promedio, row.promedioMin, row.promedioMax);
                    return (
                      <tr
                        key={row.id}
                        className={`border-t border-gray-100 ${
                          alert === "high"
                            ? "bg-amber-50"
                            : active
                              ? "bg-green-50"
                              : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-2 font-medium text-gray-900">{row.name}</td>
                        <td className="px-4 py-2 text-right">{fmt(row.litersVaca)}</td>
                        <td className="px-4 py-2 text-right">{fmt(row.litersBufalo)}</td>
                        <td className="px-4 py-2 text-right">{fmt(row.totalLiters)}</td>
                        <td className="px-4 py-2 text-right">{fmt(row.kg)}</td>
                        <td
                          className={`px-4 py-2 text-right ${promedioAlertClass(alert)}`}
                          title={promedioAlertLabel(alert, row.promedioMin, row.promedioMax)}
                        >
                          {fmtPromedio(row.promedio)}
                          {alert === "high" ? " ↑" : alert === "low" ? " ↓" : ""}
                        </td>
                        <td className="px-4 py-2 text-right">{fmt(row.cheeseCount, 0)}</td>
                        <td className="px-4 py-2 text-right">
                          <Button size="icon" variant="ghost" title={`Producir ${row.name}`} onClick={() => openCreate(row.id)}>
                            <Plus size={16} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            <div className="sm:hidden divide-y divide-gray-100">
              {byProduct.map((row) => {
                const alert = row.promedioAlert || promedioAlertOf(row.promedio, row.promedioMin, row.promedioMax);
                return (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => openCreate(row.id)}
                  className={`w-full text-left p-3 hover:bg-gray-50 ${alert === "high" ? "bg-amber-50" : ""}`}
                >
                  <p className="font-semibold text-gray-900">{row.name}</p>
                  <p className={`text-xs mt-1 ${alert ? promedioAlertClass(alert) : "text-gray-600"}`}>
                    {fmt(row.totalLiters)} L · {fmt(row.kg)} kg · {fmtPromedio(row.promedio)}
                    {alert === "high" ? " ↑" : alert === "low" ? " ↓" : ""} ·{" "}
                    {fmt(row.cheeseCount, 0)} quesos
                  </p>
                </button>
                );
              })}
            </div>
          </div>
        </div>
        </>
        )}

        {tab === "providers" && (
        <>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
          <div className="overflow-auto max-h-[min(70vh,640px)]">
            <table className="w-full text-sm text-left hidden sm:table">
              <thead className="bg-[#e7efe9] text-gray-800 sticky top-0">
                <tr>
                  <th className="px-4 py-2 font-semibold">Proveedor</th>
                  <th className="px-4 py-2 font-semibold text-right">Recibido V</th>
                  <th className="px-4 py-2 font-semibold text-right">Recibido B</th>
                  <th className="px-4 py-2 font-semibold text-right">Usado V</th>
                  <th className="px-4 py-2 font-semibold text-right">Usado B</th>
                  <th className="px-4 py-2 font-semibold text-right">Restante V</th>
                  <th className="px-4 py-2 font-semibold text-right">Restante B</th>
                </tr>
              </thead>
              <tbody className="reveal-rows">
                {loading && providers.length === 0 ? (
                  <SkeletonRows cols={7} rows={6} />
                ) : providers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                      No hay litrajes cargados en esta semana.
                    </td>
                  </tr>
                ) : (
                  providers.map((row) => (
                    <tr key={row.id} className={`border-t border-gray-100 ${rowTone(milkStatus(row))}`}>
                      <td className="px-4 py-2 font-medium text-gray-900">{row.name}</td>
                      <td className="px-4 py-2 text-right">{fmt(row.receivedVaca)}</td>
                      <td className="px-4 py-2 text-right">{fmt(row.receivedBufalo)}</td>
                      <td className="px-4 py-2 text-right">{fmt(row.usedVaca)}</td>
                      <td className="px-4 py-2 text-right">{fmt(row.usedBufalo)}</td>
                      <td className="px-4 py-2 text-right">{fmt(row.remainingVaca)}</td>
                      <td className="px-4 py-2 text-right">{fmt(row.remainingBufalo)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div className="sm:hidden divide-y divide-gray-100 reveal-host">
              {loading && providers.length === 0 ? (
                <LoadingState count={5} label="Cargando quesos" />
              ) : (
              providers.map((row) => (
                <div key={row.id} className={`p-3 ${rowTone(milkStatus(row))}`}>
                  <p className="font-semibold text-gray-900">{row.name}</p>
                  <p className="text-xs text-gray-600 mt-1">
                    Recibido {fmt(row.receivedVaca)} V / {fmt(row.receivedBufalo)} B · Restante{" "}
                    {fmt(row.remainingVaca)} V / {fmt(row.remainingBufalo)} B
                  </p>
                </div>
              ))
              )}
            </div>
          </div>
        </div>
        </>
        )}

        {tab === "productions" && (
        <>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-auto max-h-[min(70vh,640px)]">
            <table className="w-full text-sm text-left hidden sm:table">
              <thead className="bg-[#e7efe9] text-gray-800 sticky top-0">
                <tr>
                  <th className="px-4 py-2 font-semibold">Fecha</th>
                  <th className="px-4 py-2 font-semibold">Nombre</th>
                  <th className="px-4 py-2 font-semibold">Producto</th>
                  <th className="px-4 py-2 font-semibold text-right">Vaca (L)</th>
                  <th className="px-4 py-2 font-semibold text-right">Búfala (L)</th>
                  <th className="px-4 py-2 font-semibold text-right">Total L</th>
                  <th className="px-4 py-2 font-semibold text-right">Kg</th>
                  <th className="px-4 py-2 font-semibold text-right">Promedio</th>
                  <th className="px-4 py-2 font-semibold text-right">Quesos</th>
                  <th className="px-4 py-2 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {productionDays.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center text-gray-400">
                      No hay producciones en esta semana.
                    </td>
                  </tr>
                ) : (
                  productionDays.map((group) =>
                    group.types.map((type) => {
                      const typeBounds = productPromedioBounds(
                        type.rows[0]?.product || products.find((p) => String(p._id) === String(type.id)),
                      );
                      const typeAlert = promedioAlertOf(type.promedio, typeBounds.min, typeBounds.max);
                      return (
                      <React.Fragment key={`${group.date}:${type.id}`}>
                        <tr
                          className={`border-t border-emerald-200 ${
                            typeAlert === "high" ? "bg-amber-100" : "bg-[#dce8e0]"
                          }`}
                        >
                          <td className="px-4 py-2.5 font-bold text-emerald-950 whitespace-nowrap">
                            {group.label ? `${group.label} ${fmtDate(group.date)}` : fmtDate(group.date)}
                          </td>
                          <td className="px-4 py-2.5 font-semibold text-emerald-950" colSpan={2}>
                            {type.name}
                            <span className="block text-[11px] font-medium text-emerald-800/80">
                              Promedio del tipo
                              {typeAlert === "high" ? " · alto" : typeAlert === "low" ? " · bajo" : ""}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-semibold text-emerald-950">
                            {fmt(type.litersVaca)}
                          </td>
                          <td className="px-4 py-2.5 text-right font-semibold text-emerald-950">
                            {fmt(type.litersBufalo)}
                          </td>
                          <td className="px-4 py-2.5 text-right font-semibold text-emerald-950">
                            {fmt(type.totalLiters)}
                          </td>
                          <td className="px-4 py-2.5 text-right font-semibold text-emerald-950">
                            {fmt(type.kg)}
                          </td>
                          <td
                            className={`px-4 py-2.5 text-right font-bold ${
                              typeAlert ? promedioAlertClass(typeAlert) : "text-emerald-900"
                            }`}
                            title={promedioAlertLabel(typeAlert, typeBounds.min, typeBounds.max)}
                          >
                            {fmtPromedio(type.promedio)}
                            {typeAlert === "high" ? " ↑" : typeAlert === "low" ? " ↓" : ""}
                          </td>
                          <td className="px-4 py-2.5 text-right font-semibold text-emerald-950">
                            {fmt(type.cheeseCount, 0)}
                          </td>
                          <td className="px-4 py-2.5" />
                        </tr>
                        {type.rows.map((row) => {
                          const alert =
                            row.promedioAlert ||
                            promedioAlertOf(row.promedio, row.promedioMin, row.promedioMax);
                          return (
                          <tr
                            key={row._id}
                            className={`border-t border-gray-100 hover:bg-gray-50 ${
                              alert === "high" ? "bg-amber-50/80" : ""
                            }`}
                          >
                            <td className="px-4 py-2 text-gray-400">{fmtDate(row.date)}</td>
                            <td className="px-4 py-2 font-medium text-gray-900">{row.name || "—"}</td>
                            <td className="px-4 py-2">{productName(row.product)}</td>
                            <td className="px-4 py-2 text-right">{fmt(row.litersVaca)}</td>
                            <td className="px-4 py-2 text-right">{fmt(row.litersBufalo)}</td>
                            <td className="px-4 py-2 text-right">{fmt(row.totalLiters)}</td>
                            <td className="px-4 py-2 text-right">{fmt(row.kg)}</td>
                            <td
                              className={`px-4 py-2 text-right ${promedioAlertClass(alert)}`}
                              title={promedioAlertLabel(alert, row.promedioMin, row.promedioMax)}
                            >
                              {fmtPromedio(row.promedio)}
                              {alert === "high" ? " ↑" : alert === "low" ? " ↓" : ""}
                            </td>
                            <td className="px-4 py-2 text-right">{fmt(row.cheeseCount, 0)}</td>
                            <td className="px-4 py-2">
                              <div className="flex justify-end gap-1">
                                <Button size="icon" variant="ghost" onClick={() => openEdit(row)} title="Editar">
                                  <Pencil size={16} />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => handleDelete(row)} title="Eliminar">
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </React.Fragment>
                      );
                    }),
                  )
                )}
              </tbody>
            </table>
            <div className="sm:hidden divide-y divide-gray-100">
              {productionDays.map((group) =>
                group.types.map((type) => {
                  const typeBounds = productPromedioBounds(
                    type.rows[0]?.product || products.find((p) => String(p._id) === String(type.id)),
                  );
                  const typeAlert = promedioAlertOf(type.promedio, typeBounds.min, typeBounds.max);
                  return (
                  <div key={`${group.date}:${type.id}`}>
                    <div className={`px-3 py-2.5 ${typeAlert === "high" ? "bg-amber-100" : "bg-[#dce8e0]"}`}>
                      <p className="font-bold text-emerald-950">
                        {group.label ? `${group.label} ${fmtDate(group.date)}` : fmtDate(group.date)}
                      </p>
                      <p className="text-sm font-semibold text-emerald-900 mt-0.5">{type.name}</p>
                      <p className={`text-xs mt-1 ${typeAlert ? promedioAlertClass(typeAlert) : "text-emerald-800"}`}>
                        {fmt(type.cheeseCount, 0)} quesos · {fmt(type.kg)} kg · {fmtPromedio(type.promedio)}
                        {typeAlert === "high" ? " ↑" : typeAlert === "low" ? " ↓" : ""}
                      </p>
                    </div>
                    {type.rows.map((row) => {
                      const alert =
                        row.promedioAlert ||
                        promedioAlertOf(row.promedio, row.promedioMin, row.promedioMax);
                      return (
                      <div key={row._id} className={`p-3 ${alert === "high" ? "bg-amber-50/80" : ""}`}>
                        <div className="flex justify-between gap-2">
                          <div>
                            <p className="font-semibold text-gray-900">{row.name || productName(row.product)}</p>
                            <p className="text-xs text-gray-500">
                              {fmtDate(row.date)}
                              {row.name ? ` · ${productName(row.product)}` : ""}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" onClick={() => openEdit(row)}>
                              <Pencil size={16} />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDelete(row)}>
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                        <p className={`text-xs mt-1 ${alert ? promedioAlertClass(alert) : "text-gray-600"}`}>
                          {fmt(row.totalLiters)} L · {fmt(row.kg)} kg · {fmtPromedio(row.promedio)}
                          {alert === "high" ? " ↑" : alert === "low" ? " ↓" : ""} ·{" "}
                          {fmt(row.cheeseCount, 0)} quesos
                        </p>
                      </div>
                      );
                    })}
                  </div>
                  );
                }),
              )}
            </div>
          </div>
        </div>
        </>
        )}

        <p className="text-sm text-gray-500 mt-2">
          {productions.length} producciones · {byProduct.length} productos · {providers.length} proveedores con leche
        </p>
      </div>

      <AppModalShell
        open={formOpen}
        onClose={closeForm}
        title={editing ? "Editar producción" : "Nueva producción"}
        widthClass="max-w-4xl w-full"
        footer={
          <AppModalFooter>
            <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={closeForm}>
              Cancelar
            </Button>
            <Button type="submit" form="cheese-form" className="w-full sm:w-auto" disabled={saving}>
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </AppModalFooter>
        }
      >
        <form id="cheese-form" onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-sm text-gray-700 mb-1">Fecha</span>
              <select
                required
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-emerald-600"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              >
                {days.map((day) => (
                  <option key={day.date} value={dateKey(day.date)}>
                    {day.label} {fmtDate(day.date)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-sm text-gray-700 mb-1">Producto</span>
              <select
                required
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-emerald-600"
                value={form.product}
                onChange={(e) => setForm({ ...form, product: e.target.value })}
              >
                {products.map((product) => (
                  <option key={product._id} value={product._id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="block sm:col-span-2">
              <span className="block text-sm text-gray-700 mb-1">Proveedores</span>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {available.map((row) => {
                  const selected = nameTokens(form.name).some(
                    (token) => token.toLowerCase() === String(row.name || "").trim().toLowerCase(),
                  );
                  return (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => toggleNameProvider(row)}
                      className={`px-2.5 py-1 rounded-full text-sm border ${
                        selected
                          ? "bg-emerald-700 text-white border-emerald-700"
                          : "bg-white text-gray-700 border-gray-300 hover:border-emerald-600"
                      }`}
                    >
                      {row.name}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  maxLength={120}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-emerald-600"
                  value={form.name}
                  onChange={(e) => {
                    const next = e.target.value;
                    nameSource.current = next.trim() ? "manual" : "auto";
                    setForm({ ...form, name: next });
                  }}
                  placeholder="Se completa al elegir proveedores o al anotar litros"
                />
                <Button type="button" variant="secondary" className="shrink-0" onClick={applyLastProviders}>
                  Igual que la anterior
                </Button>
              </div>
              <span className="block text-xs text-gray-500 mt-1">
                Toca los proveedores. Si anotas litros, el nombre se arma solo. Si lo escribes a mano, se queda así hasta que lo borres.
              </span>
            </div>
            <label className="block">
              <span className="block text-sm text-gray-700 mb-1">Kg</span>
              <input
                type="text"
                inputMode="decimal"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-emerald-600"
                value={form.kg}
                onChange={(e) => setForm({ ...form, kg: e.target.value })}
                placeholder="0,00"
              />
            </label>
            <label className="block">
              <span className="block text-sm text-gray-700 mb-1">Cantidad de quesos</span>
              <input
                type="text"
                inputMode="numeric"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-emerald-600"
                value={form.cheeseCount}
                onChange={(e) => setForm({ ...form, cheeseCount: e.target.value })}
                placeholder="0"
              />
            </label>
          </div>

          <p
            className={`text-sm ${
              live.over
                ? "text-red-700"
                : live.promedioAlert === "high"
                  ? "text-amber-800"
                  : "text-gray-600"
            }`}
          >
            Usar: {fmt(live.totalVaca)} L vaca + {fmt(live.totalBufalo)} L búfala = {fmt(live.total)} L
            {"    "}Promedio: {fmtPromedio(live.promedio)}
            {live.promedioAlert === "high"
              ? ` ↑ por encima del rango (${fmtPromedioRange(live.promedioMin, live.promedioMax)})`
              : live.promedioAlert === "low"
                ? ` ↓ por debajo del rango (${fmtPromedioRange(live.promedioMin, live.promedioMax)})`
                : live.promedioMax != null || live.promedioMin != null
                  ? ` · rango ${fmtPromedioRange(live.promedioMin, live.promedioMax)}`
                  : ""}
            {live.over ? "    Hay litrajes por encima del restante." : ""}
          </p>

          <div className="overflow-auto max-h-[min(50vh,420px)] border border-gray-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Proveedor</th>
                  <th className="px-3 py-2 text-right font-semibold">Rest. vaca</th>
                  <th className="px-3 py-2 text-right font-semibold">Usar vaca</th>
                  <th className="px-3 py-2 text-right font-semibold">Rest. búfala</th>
                  <th className="px-3 py-2 text-right font-semibold">Usar búfala</th>
                  <th className="px-3 py-2 text-center font-semibold"> </th>
                </tr>
              </thead>
              <tbody>
                {available.map((row) => {
                  const useVaca = numUse(form.uses, row.id, "vaca");
                  const useBufalo = numUse(form.uses, row.id, "bufalo");
                  const restVaca = (row.remainingVaca || 0) - useVaca;
                  const restBufalo = (row.remainingBufalo || 0) - useBufalo;
                  const hasLines = Boolean(milkLines[row.id]?.length);
                  return (
                    <tr key={row.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-900">{row.name}</td>
                      <td className={`px-3 py-2 text-right ${restVaca < -0.01 ? "text-red-700" : "text-gray-600"}`}>
                        {fmt(restVaca)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="text"
                          inputMode="decimal"
                          className="w-24 border border-gray-300 rounded px-2 py-1 text-right text-sm outline-none focus:border-emerald-600"
                          value={form.uses[`${row.id}:vaca`] || ""}
                          onChange={(e) => setUse(row.id, "vaca", e.target.value)}
                        />
                      </td>
                      <td className={`px-3 py-2 text-right ${restBufalo < -0.01 ? "text-red-700" : "text-gray-600"}`}>
                        {fmt(restBufalo)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="text"
                          inputMode="decimal"
                          className="w-24 border border-gray-300 rounded px-2 py-1 text-right text-sm outline-none focus:border-emerald-600"
                          value={form.uses[`${row.id}:bufalo`] || ""}
                          onChange={(e) => setUse(row.id, "bufalo", e.target.value)}
                        />
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setMilkEditor(row)}
                          className={`inline-flex items-center gap-1 text-xs font-semibold rounded px-2 py-1 border ${
                            hasLines
                              ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                          title="Agregar leche"
                        >
                          <Calculator size={14} />
                          Agregar leche
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </form>
      </AppModalShell>
      {milkEditor ? (
        <AddMilkEditor
          key={milkEditor.id}
          provider={milkEditor}
          initialRows={milkRowsForProvider(milkEditor)}
          onClose={() => setMilkEditor(null)}
          onApply={(lines) => applyMilkLines(milkEditor, lines)}
        />
      ) : null}
      <NotifyDayDialog
        open={notifyOpen}
        title="Notificar quesos del día"
        hint="Elige el día y, si quieres, un tipo de producto. Se envía a admins por Telegram y WhatsApp."
        days={notifyDays}
        productPicker
        confirming={notifying}
        onClose={() => setNotifyOpen(false)}
        onConfirm={handleNotifyDay}
      />
    </>
  );
};

export default CheesesModule;
